/**
 * @file Util functions related to the document object.
 */

import DOMPurify from "dompurify";

/**
 * @param {string} tagName
 * @param {Document} document
 * @param {string} textContent
 * @return {HTMLElement} Reference to the created element
 */
export const injectElement = (tagName: any, document: any, textContent: any) => {
	const element = document.createElement(tagName);
	element.textContent = textContent;
	document.querySelector("body").appendChild(element);
	return element;
};

/**
 * @param {Document} document
 * @param {HTMLElement} element
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'document' implicitly has an 'any' type.
export const removeElement = (document, element) => element.remove();

/**
 * @param {Document} document
 * @param {string} styles
 * @return {HTMLElement}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'document' implicitly has an 'any' type.
export const injectStyles = (document, styles) => injectElement("style", document, styles);

/**
 * Injects and executes a script.
 * Immediately removes the script element, unlike injectStyles, for example.
 *
 * @param {Document} document
 * @param {function} fn
 * @param {Array} [args]
 * @return {void}
 * @deprecated Not allowed in MV3.
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'document' implicitly has an 'any' type.
export const injectScript = (document, fn, args = null) => {
	let renderedArgs = "";
	if (args)
		renderedArgs = (args as any).map(JSON.stringify).join(",");

	const actualCode = "(" + fn + ")(" + renderedArgs + ");";

	const scriptRef = injectElement("script", document, actualCode);
	removeElement(document, scriptRef);
};

/**
 * Simulates an event on the given element
 *
 * @param {Document} document
 * @param {Object|string} element This can be a selector
 * @param {string} event_name
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'document' implicitly has an 'any' type.
export const triggerEvent = (document, element, event_name) => {
	if (typeof element === "string")
		element = document.querySelector(element);

	if (!element)
		return;

	const event = new MouseEvent(event_name, {
		bubbles:    true,
		cancelable: false
	});

	element.dispatchEvent(event);
};

/**
 * Basic/naive way of finding out if the hash of a URL might be using a router.
 *
 * @param {string} hash
 * @return {boolean}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'hash' implicitly has an 'any' type.
export const isLocationHashUsingRouting = hash => hash.indexOf("/") > 0;

/**
 * Adapted from https://stackoverflow.com/a/2453832.
 *
 * @param {Node} a
 * @param {Node} b
 * @return {Node}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'a' implicitly has an 'any' type.
export const findCommonAncestor = (a, b) => {
// @ts-expect-error TS(7023) FIXME: 'getAncestors' implicitly has return type 'any' be... Remove this comment to see the full error message
	const getAncestors = node => {
		return node === document
			? [node]
			: [node].concat(getAncestors(node.parentNode));
	};

	// @ts-expect-error TS(7023) FIXME: 'internal' implicitly has return type 'any' becaus... Remove this comment to see the full error message
	const internal = (a: any, b, ancestorsB) => {
		if (!ancestorsB)
			ancestorsB = getAncestors(b);
		if (ancestorsB.length === 0) return null;
		else if (ancestorsB.indexOf(a) > -1) return a;
		else if (a === document) return null;
		else return internal(a.parentNode, b, ancestorsB);
	};

	// @ts-expect-error TS(2554) FIXME: Expected 3 arguments, but got 2.
	return internal(a, b);
};

/**
 * @param {string} htmlString
 * @return {string}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'htmlString' implicitly has an 'any' typ... Remove this comment to see the full error message
export const stripTags = htmlString => {
	const dom = new DOMParser().parseFromString(htmlString, "text/html");
	const textContent = dom.body.textContent ?? "";
	return textContent.trim();
};

/**
 * Simplifies HTML, so it can be compared without taking into account letter case, whitespace, or markup.
 *
 * @param {string} htmlString E.g. "<p> Hello,   <strong>there</strong>. </p> "
 * @return {string} E.g. "hello, there."
 */
export const normalizeHtml = (htmlString: string): string => {
	if (typeof htmlString !== "string")
		return "";

	htmlString = htmlString.replace(/</g, " <");

	if (DOMPurify.sanitize) // DOMPurify doesn't work as-is in node
		htmlString = DOMPurify.sanitize(htmlString);

	const dom = new DOMParser().parseFromString(htmlString, "text/html");
	const textContent = dom.body.textContent ?? "";

	return textContent
		.replace(/\s+/g, " ")
		.trim()
		.toLowerCase();
};

/**
 * Returns the first element that can be focused with .focus() that is a child
 * of the argument, or if no argument is provided, the entire document.
 *
 * @param {HTMLElement} element
 * @return {HTMLElement}
 */
export const findFirstFocusableElement = (element: Element | null = null) => {
	const focusableElems = getFocusableElements(element);

	if (focusableElems.length > 0)
		return focusableElems[0];
	else
		return document.body;
};

export const getFocusableElements = (element: Element | null = null) => {
	const rootNode = element ? element : document.body;
	const notNegTabIndex = ':not([tabindex="-1"])';

	const selector = focusableNodeNames.reduce((accumulator, nodeName, i) => {
		const accString = i === 0 ? "" : `${accumulator}, `;
		const nodeNameLC = nodeName.toLowerCase();
		return `${accString}${nodeNameLC}${notNegTabIndex}`;
	}, "");

	const focusable = Array.from(rootNode.querySelectorAll(selector))
		.filter(elem => {
			// Only choose visible elements
			return window.getComputedStyle(elem).visibility !== "hidden";
		});

	return focusable;
};

export const focusableNodeNames = [
	"A",
	"BUTTON",
	"INPUT",
	"SELECT",
	"TEXTAREA",

	// Custom PN Elements
	"PN-BUTTON",
	"PN-ICON-BUTTON",
	"PN-LABELED-TEXT-INPUT",
	"PN-RTE",
	"PN-SELECT"
];

/**
 *  Mimicks the closest() function, but crosses shadow boundaries
 *
 * @param selectors CSS selector string
 * @param baseElement element to start the search from
 * @returns {Element | null}
 */
export const closestElement = (selectors: string, baseElement: Element): Element | null => {
	if (baseElement === undefined || baseElement === null)
		return null;

	const condition1 = baseElement?.closest(selectors);
	const condition2 = closestElement(selectors, (baseElement.getRootNode() as ShadowRoot).host);

	const element = condition1 ?? condition2;
	return element;
};

export default {
	injectElement,
	injectStyles,
	injectScript,
	removeElement,
	triggerEvent,
	isLocationHashUsingRouting,
	findCommonAncestor,
	stripTags,
	normalizeHtml,
	findFirstFocusableElement,
	closestElement,
	focusableNodeNames
};
